import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import { Dialog } from '../nursery/accounts/AccountBits';
import purchaseOrderService from '../../services/purchaseOrders';
import { errorMessage } from '../../utils/apiResponse';
import type { PurchaseOrder, Supplier } from '../../types/purchaseOrders';

/**
 * An Accounts Admin corrects an order's supplier, dates and notes, whoever
 * raised it. Mostly for an order raised with no supplier, or a typed one,
 * which cannot go to Xero until it names a supplier on the list. An order out
 * with the supplier goes to Xero again once saved. The farm (it is in the
 * number) and the lines are not changed here.
 */
export default function EditOrderDetailsDialog({ po, onClose, onSaved }: {
  po: PurchaseOrder;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [supplierId, setSupplierId] = useState<number | null>(po.supplier_id ?? null);
  const [typedName, setTypedName] = useState(po.supplier_id ? '' : (po.supplier_name ?? ''));
  const [query, setQuery] = useState('');
  const [orderedDate, setOrderedDate] = useState(po.ordered_date?.slice(0, 10) ?? '');
  const [expected, setExpected] = useState(po.expected_delivery_date?.slice(0, 10) ?? '');
  const [notes, setNotes] = useState(po.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    purchaseOrderService.listSuppliers()
      .then((res) => { if (!cancelled) setSuppliers(res.success && res.data ? res.data : []); })
      .catch(() => { if (!cancelled) setSuppliers([]); });
    return () => { cancelled = true; };
  }, []);

  const billed = po.xero_status === 'BILLED';
  const chosen = suppliers?.find((s) => s.id === supplierId) ?? null;
  const q = query.trim().toLowerCase();
  const results = (suppliers ?? [])
    .filter((s) => !q || s.name.toLowerCase().includes(q))
    .slice(0, 50);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await purchaseOrderService.updateDetails(po.id, {
        ...(billed ? {} : { supplier_id: supplierId, supplier_name: supplierId ? null : (typedName.trim() || null) }),
        ordered_date: orderedDate || null,
        expected_delivery_date: expected || null,
        notes: notes.trim() || null,
      });
      if (res.success) onSaved();
      else setError(errorMessage(res, 'Could not save the order.'));
    } catch (e) {
      setError(errorMessage(e, 'Could not save the order.'));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <Dialog
      title="Edit order"
      subtitle={po.reference_number ?? `PO #${po.id}`}
      onClose={() => { if (!saving) onClose(); }}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </>
      )}
    >
      {error && <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">{error}</p>}

      <div>
        <p className="text-sm font-medium text-neutral-800 mb-1">Supplier</p>
        {billed ? (
          <p className="text-sm text-neutral-600 break-words">
            {po.supplier_org_name ?? po.supplier_name ?? 'No supplier'}. Xero has billed this order, so its supplier
            is fixed. Correct the bill in Xero.
          </p>
        ) : supplierId ? (
          <div className="flex items-start justify-between gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
            <span className="text-sm font-medium text-indigo-900 break-words min-w-0">
              {chosen?.name ?? po.supplier_org_name ?? po.supplier_name}
            </span>
            <button type="button" aria-label="Choose another supplier" className="text-indigo-400 hover:text-indigo-600 shrink-0"
              onClick={() => { setSupplierId(null); setQuery(''); }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} className={inputCls}
              aria-label="Search your suppliers"
              placeholder={suppliers === null ? 'Reading suppliers…' : suppliers.length ? 'Search your suppliers…' : 'No suppliers on the list yet'} />
            {results.length > 0 && (
              <ul className="max-h-48 overflow-y-auto border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                {results.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => { setSupplierId(s.id); setTypedName(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 break-words">
                      {s.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {typedName && (
              <p className="text-xs text-amber-700 break-words">
                Typed on the order as &ldquo;{typedName}&rdquo;, which is not on your supplier list. Choose it from the
                list to send the order to its own Xero contact, or{' '}
                <Link to="/accounts/suppliers" className="underline">add it under Accounts → Suppliers</Link>.
              </p>
            )}
            {!typedName && !po.supplier_name && (
              <p className="text-xs text-amber-700 break-words">The order has no supplier, so it cannot go to Xero.</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block min-w-0">
          <span className="text-sm font-medium text-neutral-800">Ordered</span>
          <input type="date" value={orderedDate} onChange={(e) => setOrderedDate(e.target.value)} className={`${inputCls} mt-1`} />
        </label>
        <label className="block min-w-0">
          <span className="text-sm font-medium text-neutral-800">Expected delivery</span>
          <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className={`${inputCls} mt-1`} />
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-neutral-800">Notes</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={`${inputCls} mt-1`} />
      </label>

      {po.status !== 'draft' && po.status !== 'cancelled' && (
        <p className="text-xs text-neutral-500 break-words">
          If purchase orders go to Xero, the order is sent there again with these details once saved.
        </p>
      )}
    </Dialog>
  );
}
